import { takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { QuoteModel } from './models/quote-model';
import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { SharedModule } from '@shared/module/shared.module';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { BaseComponent } from '@shared/components/base/base.component';
import { LikesService } from '@shared/service/http_services/like.service';
import { CreateLikeModel } from '@shared/model/customerModels/like-model';
import { QuoteItemComponent } from './components/quote-item/quote-item.component';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { HeaderComponent, HeaderConfigModel } from '@shared/components/header/header.component';
import { QuoteAggrigatorService } from '@app/pages/quotes/services/quote-aggrigator.service';
import { GetUserDataPipe } from '@shared/components/User-Activity-Info/pipes/get-user-data/get-user-data.pipe';
import { BehkhaanBookCardComponent } from '@app/pages/books/components/behkhaan-book-card/behkhaan-book-card.component';
import { ActivityLikedUsersComponent } from '@shared/components/activity-liked-users/activity-liked-users.component';
import { UserActivityInfoComponent } from '@shared/components/User-Activity-Info/user-activity-info/user-activity-info.component';
import { FooterItemPipe } from '@shared/components/Activities/pipes/footer-item.pipe';
import { ActivityFooterPluseComponent } from '@shared/components/Activities/activity-footer-pluse/activity-footer-plus.component';

import { LocalRepository } from '@shared/helper/local-repository';
import { BookmarkHandlerService, CreateBookmarkModel } from '@shared/service/http_services/handler/bookmark-handler.service';
import { RestrictionMessageDialogComponent } from '@shared/components/dialogs/restriction-message-dialog/restriction-message-dialog/restriction-message-dialog.component';

@Component({
  selector: 'app-quotes',
  imports: [
    SharedModule,
    FooterItemPipe,
    GetUserDataPipe,
    HeaderComponent,
    QuoteItemComponent,
    InfiniteScrollDirective,
    UserActivityInfoComponent,
    ActivityFooterPluseComponent,
    BehkhaanBookCardComponent,
  ],
  templateUrl: './quotes.component.html',
  styleUrls: ['./quotes.component.scss']
})
export class QuotesComponent extends BaseComponent implements OnInit {
  private _dialog = inject(MatDialog);
  private likesService = inject(LikesService);
  private _bottomSheet = inject(MatBottomSheet);
  private activatedRoute = inject(ActivatedRoute);
  private _localRepository = inject(LocalRepository);
  private quoteServiceAggregator = inject(QuoteAggrigatorService);
  private bookmarkHandlerService = inject(BookmarkHandlerService);

  quotsTermFilter: TermFilterModel = new TermFilterModel(0, 20);
  loading = true;
  itemId!: string;
  headerConfig: HeaderConfigModel = new HeaderConfigModel();
  hasMoreQuots: any;
  quotes: QuoteModel[] = [];
  allBehkhaanQuotes!: boolean;
  userQuotes!: boolean;
  bookQuotes!: boolean;
  loadingMore!: boolean;

  constructor() {
    super();
    this.headerConfig.title = 'بریده‌ کتاب‌ها';
  }

  ngOnInit(): void {
    const query = this.activatedRoute.snapshot.queryParams;
    if (!query?.['id']) {
      this.allBehkhaanQuotes = true;
      this.quoteServiceAggregator.getChosenQuotesWithUserLiked(this.quotsTermFilter)
        .pipe(
          takeUntil(this.unsubscribe$)
        )
        .subscribe(quotes => {
          if (!quotes.hasError) {
            this.quotes = quotes.result?.items;
            this.hasMoreQuots = quotes.result?.hasNextPage;
          }
          this.loading = false;
        });
    }
    else {
      this.allBehkhaanQuotes = false;
      this.itemId = query?.['id'];
      if (query?.['T'] === 'user') {
        this.userQuotes = true;
        this.quoteServiceAggregator.getGuestQuotes(this.itemId, this.quotsTermFilter)
          .pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe(quotes => {
            if (!quotes.hasError) {
              this.quotes = quotes.result?.items;
              this.headerConfig.title = `بریده‌های کتاب ${quotes.result?.items[0].actor?.fullName}`;
              this.hasMoreQuots = quotes.result?.hasNextPage;
            }
            this.loading = false;
          });
      }
      else {
        this.bookQuotes = true;
        this.quoteServiceAggregator.getQuotesOnWorkByWorkId(this.itemId, this.quotsTermFilter)
          .pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe(quotes => {
            if (!quotes.hasError) {
              this.quotes = quotes.result?.items;
              this.headerConfig.title = `بریده‌های کتاب ${quotes.result?.items[0].book?.name}`;
              this.hasMoreQuots = quotes.result?.hasNextPage;
            }
            this.loading = false;
          });
      }
    }
  }

  loadMore() {
    if (this.hasMoreQuots) {
      this.loadingMore = true;
      this.quotsTermFilter.pgNumber++;
      if (this.allBehkhaanQuotes) {
        this.quoteServiceAggregator.getChosenQuotesWithUserLiked(this.quotsTermFilter)
          .pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe(quotes => {
            if (quotes.hasError) return;
            this.pushDataToArray(quotes.result?.items);
            this.handleLocalVariables(quotes.result?.hasNextPage);
          });
      }
      else if (this.userQuotes) {
        this.quoteServiceAggregator.getGuestQuotes(this.itemId, this.quotsTermFilter)
          .pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe(quotes => {
            if (quotes.hasError) return;
            this.pushDataToArray(quotes.result?.items);
            this.handleLocalVariables(quotes.result?.hasNextPage);
          });
      }
      else {
        this.quoteServiceAggregator.getQuotesOnWorkByWorkId(this.itemId, this.quotsTermFilter)
          .pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe(quotes => {
            if (quotes.hasError) return;
            this.pushDataToArray(quotes.result?.items);
            this.handleLocalVariables(quotes.result?.hasNextPage);
          });

      }
    }
  }

  openUsersDialog(postId: string) {
    this.utilFunctions.peopleLiked(postId, this._bottomSheet, this._dialog, this._localRepository, ActivityLikedUsersComponent);
  }

  likeItem(likedObject: CreateLikeModel) {
    this.likesService
      .createLike(likedObject, this.currentUser?.id || "").pipe(
        tap(res => {
          if (res?.hasError) {
            likedObject.userLiked = false;
            this._dialog.open(RestrictionMessageDialogComponent, {
              panelClass: 'restrict-message-dialog',
              data: likedObject,
            })
          }
        }),
      )
      .subscribe();
  }
  pushDataToArray(items: QuoteModel[]) {
    items.forEach(item => {
      this.quotes.push(item);
    })
  }

  handleLocalVariables(nextPage: boolean) {
    this.hasMoreQuots = nextPage;
    this.loading = false;
    this.loadingMore = false;
  }

  bookmarkItem(bookmarkData: CreateBookmarkModel,) {
    this.bookmarkHandlerService.handleBookmark(bookmarkData)
      .subscribe(res => {
        if (bookmarkData.userMarked) {
          res.hasError ? bookmarkData.object.userMarked = true : null;
        }
        else {
          res.hasError ? bookmarkData.object.userMarked = false : null;
        }
      });
  }

}
